import { useEffect, useState } from "react";
import { api } from "../../api";
import { useConsoleTown } from "./ConsoleLayout";

export default function ConsoleSettings() {
  const { town, reloadTowns } = useConsoleTown();
  const [name, setName] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [taxRate, setTaxRate] = useState(0);
  const [timeScale, setTimeScale] = useState(1);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.consoleOverview(town.id).then((result) => {
      setName(result.town.name);
      setAnnouncement(result.town.announcement || "");
      setTaxRate(Number(result.town.taxRate || 0));
      setTimeScale(Number(result.town.timeScale || 1));
    }).catch((reason) => setError(reason.message || "设置加载失败"));
  }, [town.id]);

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await api.consoleUpdateSettings(town.id, { name, announcement, taxRate, timeScale });
      await reloadTowns();
      setMessage("设置已保存。");
    } catch (reason: any) {
      if (reason.code === "insufficient_balance") {
        setError(`余额不足：需要 ${Number(reason.needed || 0).toLocaleString()}，当前 ${Number(reason.balance || 0).toLocaleString()}。`);
      } else {
        setError(reason.message || "保存失败");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleSleep = async () => {
    try {
      if (town.status === "sleeping") await api.consoleWake(town.id);
      else await api.consoleSleep(town.id);
      await reloadTowns();
      setMessage(town.status === "sleeping" ? "小镇已唤醒。" : "小镇进入休眠，居民时间已暂停。");
    } catch (reason: any) {
      setError(reason.message || "状态切换失败");
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-black">小镇设置</h1>
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {message && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</div>}

      <section className="rounded-xl border border-[#ddd7c4] bg-white p-5 shadow-sm">
        <h2 className="font-bold">基本信息</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1.5 block font-medium text-[#665f4f]">小镇名称</span>
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={30} className="w-full rounded-lg border border-[#d8d2bf] px-3 py-2.5 outline-none focus:border-[#2f6e5b]" />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="mb-1.5 block font-medium text-[#665f4f]">公告</span>
            <textarea value={announcement} onChange={(event) => setAnnouncement(event.target.value)} maxLength={500} rows={3} className="w-full resize-y rounded-lg border border-[#d8d2bf] px-3 py-2.5 outline-none focus:border-[#2f6e5b]" />
          </label>
        </div>
      </section>

      <section className="rounded-xl border border-[#ddd7c4] bg-white p-5 shadow-sm">
        <h2 className="font-bold">经济</h2>
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm"><span>税率</span><span className="font-bold">{Math.round(taxRate * 100)}%</span></div>
          <input type="range" min="0" max="0.3" step="0.01" value={taxRate} onChange={(event) => setTaxRate(Number(event.target.value))} className="mt-3 w-full" />
          {taxRate > 0.2 && <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">高税率将吓退新居民，请谨慎调整。</div>}
        </div>
      </section>

      <section className="rounded-xl border border-[#ddd7c4] bg-white p-5 shadow-sm">
        <h2 className="font-bold">时间法则</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {[1, 2, 3].map((scale) => (
            <button key={scale} onClick={() => setTimeScale(scale)} className={`rounded-lg border px-4 py-3 text-sm font-bold ${timeScale === scale ? "border-[#2f6e5b] bg-[#e0eee8] text-[#245849]" : "border-[#d8d2bf] bg-white text-[#665f4f]"}`}>{scale}x {scale === 1 ? "正常" : "加速"}</button>
          ))}
        </div>
        <p className="mt-3 text-xs text-[#77705f]">调整到更高流速前，系统会检查余额是否至少覆盖 3 天消耗。</p>
      </section>

      <div className="flex flex-wrap gap-3">
        <button disabled={saving} onClick={save} className="rounded-lg bg-[#2f6e5b] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{saving ? "保存中..." : "保存设置"}</button>
        <button onClick={toggleSleep} className="rounded-lg border border-[#cfc8b5] bg-white px-5 py-2.5 text-sm font-bold">{town.status === "sleeping" ? "唤醒小镇" : "休眠小镇"}</button>
      </div>
    </div>
  );
}
